import { Plus } from "lucide-react";
import React from "react";
import { FieldArray, useFormikContext } from "formik";
import { Trash2 } from "lucide-react";
import MultiSelectChips from "../../components/forms/multi-chip-select";

// Static dropdown sources (customize as needed)
const DEPARTMENTS = [
  { value: "finance", label: "Finance" },
  { value: "hr", label: "HR" },
  { value: "it", label: "IT" },
  { value: "ops", label: "Operations" },
];

const ROLES = [
  { value: "manager", label: "Manager" },
  { value: "lead", label: "Lead" },
  { value: "analyst", label: "Analyst" },
  { value: "approver", label: "Approver" },
];

// User directory used by chips dropdown
const USERS = [
  { value: "pranay", label: "Pranay" },
  { value: "avinash", label: "Avinash" },
  { value: "abhiram", label: "Abhiram" },
  { value: "sneha", label: "Sneha" },
  { value: "ravi", label: "Ravi" },
  { value: "ravi_1", label: "Ravi_1" },
  { value: "ravi_2", label: "Ravi_2" },
  { value: "ravi_3", label: "Ravi_3" },
  { value: "ravi_4", label: "Ravi_4" },
  { value: "ravi_5", label: "Ravi_5" },
  { value: "ravi_6", label: "Ravi_6" },
  { value: "ravi_7", label: "Ravi_7" },
  { value: "ravi_8", label: "Ravi_8" },
];

function OriginatorTable() {
  const { values, setFieldValue } = useFormikContext();

  return (
    <FieldArray
      name="originators"
      render={({ push, remove }) => (
        <div className="w-full">
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() =>
                push({ department: "", role: "", users: [] })
              }
              className="bg-primary text-white px-3 py-2 rounded-md text-sm shadow-sm hover:opacity-95"
            >
              + Add Originator
            </button>
          </div>
          <div className="rounded-lg border border-gray-200 overflow-visible">
            <div className="max-h-[320px] overflow-visible">
              <table className="w-full text-sm text-black">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr className="[&>th]:text-left [&>th]:font-medium [&>th]:text-gray-600">
                    <th className="px-4 py-3 w-[24%]">Department</th>
                    <th className="px-4 py-3 w-[24%]">Role</th>
                    <th className="px-4 py-3">Users</th>
                    <th className="px-4 py-3 w-[80px]">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {(values.originators || []).map((row, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="px-4 py-3 align-top">
                        <div className="relative">
                          <select
                            className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 pr-8 text-sm
                                       focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                            value={row.department}
                            onChange={(e) =>
                              setFieldValue(`originators[${idx}].department`, e.target.value)
                            }
                          >
                            <option value="" disabled>Select</option>
                            {DEPARTMENTS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">▾</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="relative">
                          <select
                            className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 pr-8 text-sm
                                       focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                            value={row.role}
                            onChange={(e) =>
                              setFieldValue(`originators[${idx}].role`, e.target.value)
                            }
                          >
                            <option value="" disabled>Select</option>
                            {ROLES.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">▾</span>
                        </div>
                      </td>

                      {/* Users (chips multi-select) */}
                      <td className="px-4 py-3 align-top">
                        <MultiSelectChips
                          name={`originators[${idx}].users`}
                          value={row.users || []}
                          onChange={(vals) =>
                            setFieldValue(`originators[${idx}].users`, vals)
                          }
                          options={USERS}
                          placeholder="Search users..."
                        />
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => remove(idx)}
                          className="p-2 rounded-md hover:bg-red-50 text-red-500"
                          aria-label="Delete row"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Empty state */}
                  {(!values.originators || values.originators.length === 0) && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                        No originators added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    />
  );
}

const Originator = () => {
  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden text-black">
      <div className="flex-1 min-h-0 overflow-auto text-white flex">
        <OriginatorTable/>
      </div>
      <div className="flex-shrink-0 h-[50px] flex items-center justify-between">
        <button className="text-primary border border-primary p-2 rounded-md text-sm flex items-center gap-1 hover:bg-primary hover:text-white transition-all duration-300 ease-in-out">
            Cancel
        </button>

        <button type="submit" className="bg-primary border border-primary text-white p-2 px-4 rounded-md text-sm flex items-center gap-1 hover:bg-white hover:text-primary transition-all duration-300 ease-in-out">
          Save
        </button>
      </div>
    </div>
  );
};

export default Originator;
